import React, { useState, ReactElement } from 'react';
import ReactHtmlParser from 'react-html-parser';
import * as Yup from 'yup';

import { RecaptchaSize } from '../../types';
import useTmsApi from '../../hooks/useTmsApi';
import Form from '../Form/Form';
import {
  FormFieldCssClassType,
  FormFieldInputType,
  FormFieldT,
  FormFieldType,
  FormData,
} from '../Form/types';
import regexList from '../../regex/regexList';
import { BladeSection, CombinatorSectionItem, PageSectionFormField } from '../../ContentfulTypes';

interface Props {
  formFields: PageSectionFormField[];
  heading: BladeSection;
  cta: BladeSection;
  successSection: CombinatorSectionItem;
  errorSection?: CombinatorSectionItem;
  renderSuccessCta?: () => ReactElement;
  recaptchaSize?: RecaptchaSize;
}

const EmailSignupForm = ({
  formFields,
  heading,
  cta,
  successSection,
  errorSection,
  renderSuccessCta,
  recaptchaSize = RecaptchaSize.NORMAL,
}: Props) => {
  // DATA SUBMISSION
  const [messageData, setMessageData] = useState<FormData | null>(null);
  const { error, loading } = useTmsApi('/contact/keep-in-touch', messageData);
  // Send validated form to server to be emailed to talktous@themobileshop.ca
  const onSubmit = (data) => {
    setMessageData(data);
  };

  // Form validation
  const validationSchema = Yup.object().shape({
    customerFirstName: Yup.string()
      .matches(
        regexList.customerName,
        ReactHtmlParser(formFields[0]).errorMessage,
      )
      .required(ReactHtmlParser(formFields[0]).errorMessage),
    customerLastName: Yup.string()
      .matches(
        regexList.customerName,
        ReactHtmlParser(formFields[1]).errorMessage,
      )
      .required(ReactHtmlParser(formFields[1]).errorMessage),
    customerEmail: Yup.string()
      .email(ReactHtmlParser(formFields[0]).errorMessage)
      .required(ReactHtmlParser(formFields[0]).errorMessage),
    customerPhone: Yup.string()
      .matches(regexList.phone, ReactHtmlParser(formFields[3]).errorMessage)
      .min(10, ReactHtmlParser(formFields[3]).errorMessage)
      .required(ReactHtmlParser(formFields[3]).errorMessage),
    customerOptIn: Yup.bool().oneOf([true], 'required').required(),
    gRecaptchaResponse: Yup.string()
      .required(ReactHtmlParser(formFields[5]).errorMessage)
      .nullable(),
  });

  const fields: FormFieldT[] = [
    {
      type: FormFieldType.SECTION_HEADING,
      heading,
    },
    {
      ...formFields[0],
      __typename: formFields[0].__typename,
      contentful_id: formFields[0].contentful_id,
      type: FormFieldType.TEXTFIELD,
      inputType: FormFieldInputType.INPUT,
      maxLength: 25,
      minLength: 2,
      className: FormFieldCssClassType.HALF,
      showError: true,
      errorMessage: formFields[0].errorMessage,
      schemaName: formFields[0].fieldName,
    },
    {
      ...formFields[1],
      __typename: formFields[1].__typename,
      contentful_id: formFields[1].contentful_id,
      type: FormFieldType.TEXTFIELD,
      inputType: FormFieldInputType.INPUT,
      maxLength: 25,
      minLength: 2,
      className: FormFieldCssClassType.HALF,
      showError: true,
      errorMessage: formFields[1].errorMessage,
      schemaName: formFields[1].fieldName,
    },
    {
      ...formFields[2],
      __typename: formFields[2].__typename,
      contentful_id: formFields[2].contentful_id,
      type: FormFieldType.TEXTFIELD,
      inputType: FormFieldInputType.EMAIL,
      maxLength: 320,
      minLength: 3,
      className: FormFieldCssClassType.HALF,
      showError: true,
      errorMessage: formFields[2].errorMessage,
      schemaName: formFields[2].fieldName,
    },
    {
      ...formFields[3],
      __typename: formFields[3].__typename,
      contentful_id: formFields[3].contentful_id,
      type: FormFieldType.TEXTFIELD,
      inputType: FormFieldInputType.TEL,
      maxLength: 20,
      minLength: 10,
      className: FormFieldCssClassType.HALF,
      showError: true,
      errorMessage: formFields[3].errorMessage,
      schemaName: formFields[3].fieldName,
    },
    {
      ...formFields[4],
      __typename: formFields[4].__typename,
      contentful_id: formFields[4].contentful_id,
      type: FormFieldType.CHECKBOX,
      inputType: FormFieldInputType.CHECKBOX,
      className: FormFieldCssClassType.OPTIN,
      showError: true,
      errorMessage: formFields[4].errorMessage,
      schemaName: 'customerOptIn',
      fieldName: 'customerOptIn',
    },
    {
      ...formFields[5],
      __typename: formFields[5].__typename,
      contentful_id: formFields[5].contentful_id,
      type: FormFieldType.RECAPTCHA,
      className: FormFieldCssClassType.RECAPTCHA,
      showError: true,
      errorMessage: formFields[5].errorMessage,
      schemaName: 'gRecaptchaResponse',
      fieldName: 'gRecaptchaResponse',
    },
    {
      __typename: cta.__typename,
      contentful_id: cta.contentful_id,
      type: FormFieldType.SUBMIT,
      className: FormFieldCssClassType.SUBMIT,
      displayLabel: cta?.ctaLabel?.ctaLabel,
      fieldName: 'formContactSubmit',
      errorMessage: errorSection?.errorMessage,
      schemaName: 'formContactSubmit',
    },
  ];

  return (
    <Form
      formFields={fields}
      successSection={successSection}
      generalError={errorSection}
      renderSuccessCta={renderSuccessCta}
      recaptchaSize={recaptchaSize}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      error={error}
      loading={loading}
      formData={messageData}
      setFormData={setMessageData}
      testId="email-sign-up"
    />
  );
};

export default EmailSignupForm;
